import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  TextField,
  MenuItem,
  Typography,
  Grid,
  Paper,
  Select,
} from "@mui/material";
import DashboardContainer from "../../components/dashboard-container";
import "react-calendar/dist/Calendar.css";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { trackingService } from "../../utils/services/trackingService";
import { eventService } from "../../utils/services/eventService";
import AutocompleteWithPagination from "../../components/autocomplete-with-pagination";
import { toast } from "react-toastify";
import ClientAutocomplete from "../../components/clientAutocomplete";
import { clientService } from "../../utils/services/clientService";
import { inputFieldSx, selectFieldSx } from "../../constants/fieldStyles";

const init_data = {
  assetType: "",
  identifier: "",
  eventCode: "",
  eventDescription: "",
  eventDateTime: null,
  eventLocation: "",
  comment: "",
  logistics_leg: "",
};
const ManualTracking = () => {
  const [formData, setFormData] = useState({});
  const logisticsLegRef = useRef(formData.logistics_leg);
  const [selectedClient, setSelectedClient] = useState(null);

  const client_id = localStorage.getItem("client_id");
  useEffect(() => {
    if (client_id) {
      setSelectedClient({ _id: client_id });
    }
  }, [client_id]);

  useEffect(() => {
    setFormData(init_data);
  }, []);

  const handleChange = (event) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
  };

  const isFormValid = () => {
    return (
      formData.assetType &&
      formData.identifier &&
      formData.logistics_leg &&
      formData.eventCode &&
      formData.eventDateTime &&
      formData.eventLocation &&
      selectedClient &&
      selectedClient._id
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    try {
      let obj = {
        asset: formData.assetType,
        id: formData.identifier,
        data: {
          event_code: formData.eventCode,
          description: formData.eventDescription,
          event_date_time: formData.eventDateTime,
          location: formData.eventLocation,
          comment: formData.comment,
        },
        client: selectedClient?._id || null,
      };
      trackingService.publish(obj);
      toast.success("Tracking event published successfully!");
      setFormData({ ...init_data });
      setSelectedClient(client_id ? { _id: client_id } : null);
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const requiredFieldStyle = {
    fontWeight: "medium",
    textAlign: "start",
    mb: 1,
    "& .required-star": {
      color: "#DC585C",
      ml: 0.5,
    },
  };

  const updateEventCodeAndDescription = async (e) => {
    handleChange({
      target: {
        name: "eventCode",
        value: e?._id,
      },
    });

    if (e?._id) {
      let resp = await eventService.fetch("", "", e._id);
      if (resp.total) {
        setFormData({
          ...formData,
          eventDescription: resp.data[0].description,
          eventCode: resp.data[0].code,
        });
      }
    }
  };
  return (
    <DashboardContainer
      hideAddButton
      filterButton={false}
      hideUploadButton
      header="Manual Tracking"
      hideGridList
      hideFilterClient={true}
    >
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mt: 2,
          height: "100%",
          maxHeight: { xs: "calc(100vh - 200px)", md: "calc(100vh - 170px)" },
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {!client_id && (
              <Grid item xs={12} md={12}>
                <Typography sx={requiredFieldStyle}>
                  Client <span className="required-star">*</span>
                </Typography>
                <AutocompleteWithPagination
                  onDataChange={(client) => setSelectedClient(client)}
                  hideLabel
                  width={"100%"}
                  optionKey={(item) =>
                    item.name + " (" + item.business_name + ")"
                  }
                  apiEndPoint={(limit, skip, search) =>
                    clientService.fetch(limit, skip, search, null, null, [
                      "name",
                      "business_name",
                    ])
                  }
                ></AutocompleteWithPagination>
              </Grid>
            )}
            <Grid item xs={12} md={6}>
              <Typography sx={requiredFieldStyle}>
                Type of Tracked Asset <span className="required-star">*</span>
              </Typography>
              <Select
                key={formData.assetType || "default"}
                size="small"
                fullWidth
                name="assetType"
                value={formData.assetType}
                onChange={handleChange}
                sx={{
                  textAlign: "start",
                  "& .MuiSelect-select": {
                    textAlign: "start",
                  },
                  backgroundColor: "var(--bg-input)",
                  color: "var(--text-primary)",
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      borderColor: "var(--border-default)",
                    },
                    "&:hover fieldset": {
                      borderColor: "var(--border-hover)",
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: "var(--accent)",
                    },
                  },
                }}
              >
                {[
                  "Parcel",
                  "Overpack",
                  "Manifest",
                  "Purchase Order",
                  "Bill of Lading",
                ].map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={requiredFieldStyle}>
                Identifier <span className="required-star">*</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                name="identifier"
                value={formData.identifier}
                onChange={handleChange}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={requiredFieldStyle}>
                Logistics Leg <span className="required-star">*</span>
              </Typography>
              <AutocompleteWithPagination
                selectedItem={{ _id: formData.logistics_leg }}
                onDataChange={(e) => {
                  handleChange({
                    target: {
                      name: "logistics_leg",
                      value: e?._id,
                    },
                  });
                  logisticsLegRef.current = e?._id || "";
                }}
                hideLabel
                width={"100%"}
                optionKey={"_id"}
                apiEndPoint={(limit, skip, search) =>
                  eventService.fetchLeg(search)
                }
              ></AutocompleteWithPagination>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={requiredFieldStyle}>
                Provider Event Code <span className="required-star">*</span>
              </Typography>
              <AutocompleteWithPagination
                disabled={!formData.logistics_leg}
                selectedItem={{ _id: formData.eventCode }}
                onDataChange={(e, event) =>
                  updateEventCodeAndDescription(e, event)
                }
                hideLabel
                width={"100%"}
                optionKey={"_id"}
                apiEndPoint={(limit, skip, search) =>
                  eventService.fetchCode(
                    search,
                    null,
                    limit,
                    skip,
                    logisticsLegRef.current,
                  )
                }
              ></AutocompleteWithPagination>
            </Grid>

            <Grid item xs={12} md={12}>
              <Typography sx={requiredFieldStyle}>
                Event Description <span className="required-star">*</span>
              </Typography>
              <TextField
                multiline
                disabled
                size="small"
                fullWidth
                name="eventDescription"
                value={formData.eventDescription}
                onChange={handleChange}
                sx={{
                  ...inputFieldSx,
                  "& .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "var(--bg-input)",
                    opacity: 0.6,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={requiredFieldStyle}>
                Event Date & Time <span className="required-star">*</span>
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: true,
                      sx: inputFieldSx,
                    },
                  }}
                  value={
                    formData.eventDateTime
                      ? dayjs(new Date(formData.eventDateTime))
                      : null
                  }
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      eventDateTime: dayjs(e).format("YYYY-MM-DD HH:mm:ss"),
                    });
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={requiredFieldStyle}>
                Event Location <span className="required-star">*</span>
              </Typography>
              <TextField
                size="small"
                fullWidth
                name="eventLocation"
                value={formData.eventLocation}
                onChange={handleChange}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ textAlign: "start" }}>Comment</Typography>
              <TextField
                size="small"
                fullWidth
                multiline
                rows={4}
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                sx={inputFieldSx}
              />
            </Grid>
            <Grid
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 2,
              }}
              item
              xs={12}
              gap={3}
            >
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!isFormValid()}
              >
                Publish
              </Button>
              <Button
                color="secondary"
                variant="contained"
                disabled={!isFormValid()}
                onClick={() => setFormData(init_data)}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </DashboardContainer>
  );
};

export default ManualTracking;
